import { useEffect, useRef, useState } from 'react';
import { onTill, localSettings } from '../lib/till.js';

/**
 * A keyboard of last resort.
 *
 * Windows already has one, on the taskbar, and it is the better one: it is what people know,
 * it handles every language the machine is set up for, and it is not this project's to
 * maintain. So this stays out of the way and lets Windows do it.
 *
 * The exception is full screen, which hides the taskbar and takes the Windows keyboard with
 * it. On a touch panel with no physical keys that leaves nobody able to type at all, and both
 * ways back out, F11 and the menu bar, need the keyboard that has just gone.
 *
 * This was written when pairing a till forced it full screen, so every counter had that
 * problem. That cause is fixed: a till is maximised now, the taskbar is where it always was,
 * and this appears only if somebody deliberately chooses full screen.
 *
 * Two things it must get right or it is worse than useless.
 *
 * It types the way a person does, through the element's own value setter and a real input
 * event, so React sees the change. Setting .value directly looks right and leaves the state
 * behind it untouched, which is a form that says one thing and submits another.
 *
 * And it never steals focus. Every key is a mousedown that is prevented, so the caret stays
 * where it was and the field never loses the selection under it.
 */

const LETTERS = [
  ['q', 'w', 'e', 'r', 't', 'y', 'u', 'i', 'o', 'p'],
  ['a', 's', 'd', 'f', 'g', 'h', 'j', 'k', 'l'],
  ['z', 'x', 'c', 'v', 'b', 'n', 'm']
];

const DIGITS = [
  ['1', '2', '3'],
  ['4', '5', '6'],
  ['7', '8', '9'],
  ['.', '0', '-']
];

/** Anything that takes typing, and nothing that does not. */
const takesTyping = el => {
  if (!el) return false;
  if (el.tagName === 'TEXTAREA') return true;
  if (el.tagName !== 'INPUT') return false;
  return !['checkbox', 'radio', 'button', 'submit', 'range', 'file', 'color'].includes(el.type);
};

const wantsDigits = el => el && (el.inputMode === 'numeric' || el.inputMode === 'decimal'
  || el.type === 'number' || el.type === 'tel');

/**
 * Put text into a field the way the browser would.
 *
 * React tracks the value on the DOM node itself, so assigning to .value quietly desynchronises
 * the two: the field shows what was typed and the state behind it does not have it. Going
 * through the prototype setter and dispatching a real input event is what a keypress does.
 */
function typeInto(el, next, caret) {
  const proto = el.tagName === 'TEXTAREA' ? window.HTMLTextAreaElement.prototype : window.HTMLInputElement.prototype;
  const setter = Object.getOwnPropertyDescriptor(proto, 'value')?.set;
  if (setter) setter.call(el, next); else el.value = next;
  el.dispatchEvent(new Event('input', { bubbles: true }));
  if (caret != null && el.setSelectionRange) {
    try { el.setSelectionRange(caret, caret); } catch { /* number inputs refuse this */ }
  }
}

export function Keyboard() {
  const [target, setTarget] = useState(null);
  // Whether the taskbar, and so the Windows keyboard, is reachable on this machine.
  const [needed, setNeeded] = useState(false);
  const [shift, setShift] = useState(false);
  const [hidden, setHidden] = useState(false);
  const held = useRef(null);

  useEffect(() => {
    if (!onTill) return undefined;
    localSettings().then(cfg => setNeeded(Boolean(cfg && cfg.fullScreen))).catch(() => {});

    const onFocus = e => {
      if (!takesTyping(e.target)) return;
      held.current = e.target;
      setTarget(e.target);
      setHidden(false);
    };
    // Focus moving to a key on this keyboard is not focus leaving the field, and the mousedown
    // handler prevents that anyway. This only fires when focus really goes elsewhere.
    const onBlur = () => setTimeout(() => {
      if (!takesTyping(document.activeElement)) { held.current = null; setTarget(null); }
    }, 0);

    document.addEventListener('focusin', onFocus);
    document.addEventListener('focusout', onBlur);
    return () => {
      document.removeEventListener('focusin', onFocus);
      document.removeEventListener('focusout', onBlur);
    };
  }, []);

  if (!onTill || !needed || !target || hidden) return null;

  const el = held.current;
  const digitsOnly = wantsDigits(el);

  const press = key => {
    if (!el) return;
    const start = el.selectionStart ?? el.value.length;
    const end = el.selectionEnd ?? el.value.length;

    if (key === 'back') {
      if (start !== end) typeInto(el, el.value.slice(0, start) + el.value.slice(end), start);
      else if (start > 0) typeInto(el, el.value.slice(0, start - 1) + el.value.slice(start), start - 1);
      return;
    }
    if (key === 'clear') return typeInto(el, '', 0);
    if (key === 'enter') {
      // A form submits, and anything else just closes the keyboard. Both are what pressing
      // enter on a real keyboard would do.
      const form = el.closest('form');
      if (form) form.requestSubmit ? form.requestSubmit() : form.submit();
      else el.blur();
      return;
    }

    const text = shift ? key.toUpperCase() : key;
    typeInto(el, el.value.slice(0, start) + text + el.value.slice(end), start + text.length);
    if (shift) setShift(false);
  };

  // Prevented on the way down, so the field keeps focus and its caret. Without this every key
  // moves focus to the key itself and the second one types nowhere.
  const key = (label, onPress, extra = {}) => (
    <button key={label} onMouseDown={e => e.preventDefault()} onClick={onPress}
      style={{
        minWidth: 40, height: 46, padding: '0 10px', cursor: 'pointer',
        font: '500 17px/1 var(--font-ui)', color: 'var(--color-text-primary)',
        background: 'var(--color-bg-surface)', border: '1px solid var(--color-border-default)',
        borderRadius: 'var(--radius-md)', flex: extra.grow ? 1 : 'none', ...extra.style
      }}>
      {label}
    </button>
  );

  const row = (children, gap = 6) => (
    <div style={{ display: 'flex', gap, justifyContent: 'center' }}>{children}</div>
  );

  return (
    <div className="no-print" style={{
      position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 200,
      padding: '8px 10px 10px', display: 'grid', gap: 6,
      background: 'var(--color-bg-subtle)', borderTop: '1px solid var(--color-border-strong)',
      boxShadow: '0 -8px 24px rgba(2,6,23,0.18)'
    }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '0 2px 2px' }}>
        <span className="t-label-xs muted" style={{ flex: 1 }}>
          {digitsOnly ? 'NUMBERS' : 'KEYBOARD'}
        </span>
        {key('Hide', () => setHidden(true), { style: { minWidth: 64 } })}
      </div>

      {digitsOnly
        ? DIGITS.map((r, i) => row([
            ...r.map(d => key(d, () => press(d), { grow: true, style: { height: 52, fontSize: 20 } })),
            ...(i === 0 ? [key('back', () => press('back'), { style: { minWidth: 70 } })] : []),
            ...(i === 1 ? [key('clear', () => press('clear'), { style: { minWidth: 70 } })] : []),
            ...(i === 2 ? [key('enter', () => press('enter'), { style: { minWidth: 70 } })] : [])
          ], 8))
        : (
          <>
            {row(LETTERS[0].map(k => key(shift ? k.toUpperCase() : k, () => press(k), { grow: true })))}
            {row(LETTERS[1].map(k => key(shift ? k.toUpperCase() : k, () => press(k), { grow: true })))}
            {row([
              key(shift ? 'SHIFT' : 'shift', () => setShift(v => !v),
                { style: { minWidth: 70, background: shift ? 'var(--color-accent-subtle)' : undefined } }),
              ...LETTERS[2].map(k => key(shift ? k.toUpperCase() : k, () => press(k), { grow: true })),
              key('back', () => press('back'), { style: { minWidth: 70 } })
            ])}
            {row([
              key('123', () => press(''), { style: { minWidth: 56, visibility: 'hidden' } }),
              ...['1', '2', '3', '4', '5', '6', '7', '8', '9', '0'].map(d => key(d, () => press(d), { grow: true }))
            ])}
            {row([
              key('@', () => press('@'), { style: { minWidth: 48 } }),
              key('.', () => press('.'), { style: { minWidth: 48 } }),
              key('-', () => press('-'), { style: { minWidth: 48 } }),
              key('space', () => press(' '), { grow: true }),
              key('enter', () => press('enter'), { style: { minWidth: 90 } })
            ])}
          </>
        )}
    </div>
  );
}
